import { SportMatchModel, StrongTeam } from '@nf/types/odds';
import { matchStyles } from '../match/match.style';
import { oddsPageAtom } from '@/atoms/odds-page';
import { useLocale, useTranslations } from 'next-intl';
import { useAtomValue } from 'jotai';
import { MatchOption } from './match-option';
import { cx } from 'styled-system/css';
import { getHomeTeamName, getTeamName } from '@/utils/odds-utils';
import { MatchTimer } from '@/features/sportsbook-aside/components/match-item/match-timer';
import { useRouter } from '@/navigation';
import { useAnalyseAttribute } from '@/features/next-analyse';
import { useCurrentProperties } from '@/hooks/query/use-menu-data';
import { sportsbookService } from '@/services/sportsbook.service';
import { matchService } from '@/services/match.service';
import { Breakpoint } from '@/features/breakpoint';
import { useBreakpoint } from '@/features/breakpoint/contexts';
import { setKafkaNormal } from '@/hooks/query/use-kafka-log';
import { useAppearanceStore } from '@/store/appearance';
import { ViewEnum } from '@/types';
import { KafkaNote } from '@nf/utils-common/constants';
import { useGetSiteConfigs } from '@/hooks/query/use-site-configs';
import { useGetEnableMatchList } from '@/hooks/query/use-promotion-data';
import { useGoldenDuelSetting } from '@/hooks/query/golden-duel';

interface MatchInfoProps {
	match: SportMatchModel;
}

export const MatchInfo = ({ match }: MatchInfoProps) => {
	const t = useTranslations('General');
	const oddsData = useAtomValue(oddsPageAtom);
	const { eventMode, eventName } = useCurrentProperties();
	const router = useRouter();
	const locale = useLocale();
	const { routePrefix } = useCurrentProperties();
	const taggedElement = useAnalyseAttribute(routePrefix === 'favorites' ? 'switch-to-single-match-via-my-favorites' : 'switch-to-single-match-via-odds-page');
	const view = useAppearanceStore(state => state.view);
	const { breakpoint } = useBreakpoint();
	const { SiteConfigsData: siteConfig } = useGetSiteConfigs();
	const { data: jackpotEnableMatchList } = useGetEnableMatchList();
	const { isGoldenDuelSupported } = useGoldenDuelSetting();
	const {
		IsLive,
		TeamId1,
		TeamId2,
		Neu,
		Rc1,
		Rc2,
		T1V,
		T2V,
		BetTypes,
		GameID,
		MaT,
		LeagueId,
		MatchId
	} = match;
	const strongTeam = matchService.getStrongTeam(BetTypes);

	const setKafka = () => {
		if (view == ViewEnum.mars && breakpoint <= Breakpoint.sm) {
			setKafkaNormal({
				note: KafkaNote.MarsV1,
				type: 6,
				action: 34
			});
		}
	}

	const setPromotionKafka = () => {
		// jackpot
		if (siteConfig?.EnableSingleMatchJackpot && (jackpotEnableMatchList?.find(x => x.MatchId == MatchId) ?? false)) {
			setKafkaNormal({
				note: KafkaNote.EPLJackpot,
				type: view == ViewEnum.mars ? 2 : 1,
				action: breakpoint <= Breakpoint.sm ? 8 : 22
			});
		}

		// golden duel
		if (isGoldenDuelSupported(GameID, LeagueId)) {
			setKafkaNormal({
				note: KafkaNote.GoldenDuel,
				type: 1,
				action: 45
			});
		}
	}

	const handleVisitSingleMatch = () => {
		setKafka();
		setPromotionKafka();
		const singleMatchHref = sportsbookService.makeSingleMatchPath({
			gameId: GameID,
			maT: MaT,
			leagueId: LeagueId,
			matchId: MatchId,
			eventMode: eventMode,
			eventName: eventName
		});
		router.push(singleMatchHref), { locale };
	};

	return (
		<div
			className={matchStyles.event}
			onClick={handleVisitSingleMatch}
			ref={taggedElement}
		>
			<div className={cx('match-stats', matchStyles.stats)}>
				{
					IsLive ?
						<div className={matchStyles.score}>{`${T1V} - ${T2V}`}</div> : null
				}
				<div className="match-timer">
					<MatchTimer match={match} />
				</div>
			</div>
			<div className={matchStyles.teams}>
				<div className={matchStyles.team}>
					<div className={cx(matchStyles.teamName, 'multi-line-ellipsis', strongTeam === StrongTeam.Home && matchStyles.teamStrong, GameID === 44 && matchStyles.teamRed)}>{getHomeTeamName(TeamId1, Neu, t('Neutral_Team'), oddsData?.teamNameMap)}</div>
					{
						Rc1 ?
							<div className={cx('red-card', matchStyles.redcard)}>{Rc1}</div> : null
					}
				</div>
				<div className={matchStyles.team}>
					<div className={cx(matchStyles.teamName, 'multi-line-ellipsis', strongTeam === StrongTeam.Away && matchStyles.teamStrong, GameID === 44 && matchStyles.teamBlue)}>{getTeamName(TeamId2, oddsData?.teamNameMap)}</div>
					{
						Rc2 ?
							<div className={cx('red-card', matchStyles.redcard)}>{Rc2}</div> : null
					}
				</div>

			</div>

			<MatchOption match={match} />
		</div>
	)
};